import { useEffect, useRef, useState, type ReactNode } from "react";
import { api, errorMessage } from "../api/client";
import type { Category, Product } from "../api/types";
import { BASE_SHAPE, GarmentSketch, type GarmentLook } from "../components/GarmentSketch";
import { cleanWish, emptyPiece, keptWishes, lengthsFor, pieceFit, rowDone, rowsFor, sizesFor, OPTIONAL_ROWS, type PieceFields, type Row } from "../lib/piece";

type Opt = [string, string];
export const KINDS: Partial<Record<Category, Opt[]>> = {
  pants: [
    ["jeans", "Jeans"],
    ["pants", "Pants"],
    ["shorts", "Shorts"],
  ],
  top: [
    ["tee", "Tee"],
    ["shirt", "Shirt"],
    ["knit", "Sweater"],
    ["top", "Top"],
    ["jacket", "Jacket"],
  ],
};
const SHAPES: Record<Category, Opt[]> = {
  pants: [
    ["straight", "Straight"],
    ["slim", "Slim"],
    ["skinny", "Skinny"],
    ["wide_leg", "Wide leg"],
    ["flare", "Flare"],
    ["relaxed", "Relaxed"],
    ["barrel", "Barrel"],
  ],
  top: [
    ["fitted", "Fitted"],
    ["relaxed", "Relaxed"],
    ["oversized", "Oversized"],
    ["cropped", "Cropped"],
  ],
  dress: [
    ["shift", "Shift"],
    ["a_line", "A-line"],
    ["wrap", "Wrap"],
    ["bodycon", "Bodycon"],
  ],
  skirt: [
    ["a_line", "A-line"],
    ["pencil", "Pencil"],
    ["full", "Full"],
  ],
};
const RISES: Opt[] = [
  ["high", "High"],
  ["mid", "Mid"],
  ["low", "Low"],
];
/** A few words under each drawing, for the differences a drawing alone may not explain. */
const HINT: Record<string, string> = {
  straight: "Same width down",
  slim: "Slimmer",
  skinny: "Hugs the leg",
  wide_leg: "Wide all the way",
  flare: "Opens at the hem",
  relaxed: "Roomy all over",
  barrel: "Bows at the knee",
  fitted: "Close to the body",
  oversized: "Big and boxy",
  cropped: "Ends above the waist",
  shift: "Straight, no waist",
  a_line: "Widens to the hem",
  wrap: "Ties across",
  bodycon: "Hugs the body",
  pencil: "Narrow and straight",
  full: "Lots of swing",
  high: "At your waist",
  mid: "Below the navel",
  low: "On the hips",
};
const ROW_NAME: Record<Row, string> = { brand: "Brand & size", rise: "Rise", shape: "Shape", altered: "Altered or fit wishes" };
const label = (opts: Opt[], v: string | null) => opts.find(([k]) => k === v)?.[1] ?? "";

/** The wish as would-change ids, read now if they saved before it was read. A failed read keeps it as a note. */
async function wishesFor(cat: Category, p: PieceFields): Promise<string[]> {
  const text = cleanWish(p.wish);
  if (!text) return [];
  if (p.wishFor === text) return keptWishes(p);
  const read = await api.readFitWishes({ category: cat, text }).catch(() => null);
  return (read?.would_change ?? []).filter((id) => !p.wishOff.includes(id));
}

export type Described = { product: Product; size: string; fit: string | null; wouldChange: string[]; fitWish: string | null };

/** Describe the piece to the API. The size it returns is the label as the server cleaned it. */
export async function describePiece(cat: Category, p: PieceFields): Promise<Described> {
  const fit = pieceFit(p);
  const { product } = await api.describe({
    category: cat,
    kind: p.kind,
    cut: p.shape,
    rise: cat === "pants" ? p.rise : null,
    brand: p.brand.trim() || null,
    size_label: p.size.trim(),
    fit,
    altered: p.altered ? p.alts : [],
  });
  const wouldChange = await wishesFor(cat, p);
  return { product, size: product.sizes[0]?.label ?? p.size.trim(), fit, wouldChange, fitWish: cleanWish(p.wish) || null };
}

function Tile(props: { pressed: boolean; onClick: () => void; name: string; hint?: string; children: ReactNode; kind: string }) {
  return (
    <button type="button" className={"otile t-" + props.kind} aria-pressed={props.pressed} onClick={props.onClick}>
      {props.children}
      <span className="tl">{props.name}</span>
      {props.hint && <small>{props.hint}</small>}
    </button>
  );
}

/** The piece that fits well, described instead of linked: owned for years, thrifted, altered.
 *  One short list. Brand and size are all it needs; each row folds to a one-line answer. */
export function DescribePiece(props: {
  cat: Category;
  value: PieceFields;
  onChange: (f: (p: PieceFields) => PieceFields) => void;
  brands: string[];
  tailoring: string[];
  wouldChange: { id: string; label: string }[];
}) {
  const { cat, value: p, onChange, brands, tailoring, wouldChange } = props;
  const rows = rowsFor(cat);
  const [open, setOpen] = useState<Row | null>(() => rows.find((r) => !rowDone(p, r)) ?? null);
  const [query, setQuery] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [otherSize, setOtherSize] = useState(false);
  const set = (patch: Partial<PieceFields>) => onChange((x) => ({ ...x, ...patch }));
  const unskip = (r: Row) => (x: PieceFields) => x.skipped.filter((s) => s !== r);
  const wishTimer = useRef<number | undefined>(undefined);
  const wishAsked = useRef("");
  useEffect(() => () => window.clearTimeout(wishTimer.current), []);

  const look: GarmentLook = { cat, kind: p.kind, shape: p.shape, rise: p.rise, petite: p.petite, length: p.length, curvy: p.curvy, alts: p.altered ? p.alts : [] };
  const fit = pieceFit(p);
  const sizes = sizesFor(cat, p);
  const lengths = lengthsFor(cat);
  const q = query ?? p.brand;
  const hits = brands.filter((b) => b.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 40);
  const exact = brands.some((b) => b.toLowerCase() === q.trim().toLowerCase());
  const picks = tailoring.filter((t) => t !== "Something else");

  const pickBrand = (b: string) => {
    set({ brand: b });
    setQuery(null);
    setListOpen(false);
  };
  // Their words, read into what the fit engine can allow for. They see it and can drop any of it.
  const readWish = (text: string) => {
    window.clearTimeout(wishTimer.current);
    if (!text || text === p.wishFor || text === wishAsked.current) return;
    wishAsked.current = text;
    api.readFitWishes({ category: cat, text }).then(
      ({ would_change }) => onChange((x) => (cleanWish(x.wish) === text ? { ...x, wishRead: would_change, wishFor: text } : x)),
      () => {
        wishAsked.current = ""; // try again on the next pause or on save
      },
    );
  };
  const wishText = cleanWish(p.wish);
  const wishShown = wishText ? p.wishRead : [];
  const wishLabel = (id: string) => wouldChange.find((w) => w.id === id)?.label ?? id;
  const toggleFit = (patch: Partial<PieceFields>) =>
    onChange((x) => {
      const n = { ...x, ...patch };
      return sizesFor(cat, n).includes(n.size) || otherSize ? n : { ...n, size: "" };
    });

  const summary = (r: Row): string => {
    if (r === "brand") return [p.brand.trim(), p.size.trim() && p.size.trim() + (fit ? " " + fit : "")].filter(Boolean).join(" · ");
    if (p.skipped.includes(r)) return "Skipped";
    if (r === "rise") return p.rise ? label(RISES, p.rise) + " rise" : "";
    if (r === "shape") return label(SHAPES[cat], p.shape);
    const alt = p.altered === null ? "" : p.altered ? p.alts.join(", ") || "Altered" : "Not altered";
    const wish = wishText ? wishShown.filter((id) => !p.wishOff.includes(id)).map(wishLabel).join(", ") || "Note added" : "";
    return [alt, wish].filter(Boolean).join(" · ");
  };

  const body = (r: Row) => {
    if (r === "brand")
      return (
        <>
          <div className="combo">
            <div className="seam">
              <input
                id="brand-q"
                type="text"
                autoComplete="off"
                maxLength={80}
                placeholder="Search brands"
                aria-label="Brand"
                role="combobox"
                aria-expanded={listOpen}
                aria-controls="brand-opts"
                value={q}
                onFocus={() => setListOpen(true)}
                onClick={() => setListOpen(true)}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setListOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setListOpen(false);
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (q.trim()) pickBrand(hits.find((b) => b.toLowerCase() === q.trim().toLowerCase()) ?? q.trim());
                  }
                }}
                onBlur={() => {
                  // Typed but not picked: keep what they typed, matched to a listed brand where it can be.
                  const typed = q.trim();
                  window.setTimeout(() => {
                    setListOpen(false);
                    if (query !== null && typed) pickBrand(brands.find((b) => b.toLowerCase() === typed.toLowerCase()) ?? typed);
                    else setQuery(null);
                  }, 150);
                }}
              />
            </div>
            {listOpen && (hits.length > 0 || q.trim()) && (
              <ul className="opts" id="brand-opts" role="listbox">
                {hits.map((b) => (
                  <li key={b}>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickBrand(b)}>
                      {b}
                    </button>
                  </li>
                ))}
                {q.trim() && !exact && (
                  <li className="manual">
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickBrand(q.trim())}>
                      Use “{q.trim()}”
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
          <div className="fitline">
            <label className="sw">
              <input type="checkbox" checked={p.petite} onChange={(e) => toggleFit({ petite: e.target.checked })} />
              <i />
              Petite
            </label>
            <label className="sw">
              <input type="checkbox" checked={p.curvy} onChange={(e) => set({ curvy: e.target.checked })} />
              <i />
              Curvy
            </label>
            <div className={"segs" + (p.petite ? " off" : "")} role="group" aria-label={cat === "pants" ? "Inseam" : "Length"}>
              {lengths.map((l) => (
                <button key={l} type="button" disabled={p.petite} aria-pressed={!p.petite && p.length === l} onClick={() => toggleFit({ length: l })}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <p className="eyebrow">{(fit ?? "Regular") + " sizes"}</p>
          <div className="row" role="group" aria-label="Size">
            {sizes.map((s) => (
              <button
                key={s}
                type="button"
                className="chip size"
                aria-pressed={!otherSize && p.size === s}
                onClick={() => {
                  setOtherSize(false);
                  set({ size: s });
                }}
              >
                {s}
              </button>
            ))}
            <button type="button" className="chip sm lite" aria-pressed={otherSize} onClick={() => setOtherSize(!otherSize)}>
              Other
            </button>
          </div>
          {otherSize && (
            <div className="seam">
              <input
                type="text"
                autoComplete="off"
                maxLength={20}
                autoFocus
                placeholder="Size on its tag, like 4P or 30x30"
                aria-label="Size on its tag"
                value={sizes.includes(p.size) ? "" : p.size}
                onChange={(e) => set({ size: e.target.value })}
              />
            </div>
          )}
        </>
      );
    if (r === "rise")
      return (
        <div className="otiles">
          {RISES.map(([v, name]) => (
            <Tile key={v} kind="rise" name={name} hint={HINT[v]} pressed={p.rise === v} onClick={() => onChange((x) => ({ ...x, rise: v, skipped: unskip("rise")(x) }))}>
              <GarmentSketch look={{ ...look, rise: v, alts: [] }} showRise viewBox="18 0 164 128" />
            </Tile>
          ))}
          <button type="button" className="linkish" onClick={() => onChange((x) => ({ ...x, rise: null, skipped: [...unskip("rise")(x), "rise"] }))}>
            Skip
          </button>
        </div>
      );
    if (r === "shape")
      return (
        <div className="otiles wide">
          {SHAPES[cat].map(([v, name]) => (
            <Tile key={v} kind="shape" name={name} hint={HINT[v]} pressed={p.shape === v} onClick={() => onChange((x) => ({ ...x, shape: v, skipped: unskip("shape")(x) }))}>
              <GarmentSketch
                look={{ ...look, shape: v, alts: [] }}
                compare={v === BASE_SHAPE[cat] ? null : { ...look, shape: BASE_SHAPE[cat], alts: [] }}
                viewBox={cat === "pants" && p.kind !== "shorts" ? "10 50 180 190" : "0 0 200 240"}
              />
            </Tile>
          ))}
          <button type="button" className="linkish" onClick={() => onChange((x) => ({ ...x, shape: null, skipped: [...unskip("shape")(x), "shape"] }))}>
            Skip
          </button>
        </div>
      );
    return (
      <>
        <p className="eyebrow">Altered?</p>
        <div className="row" role="group" aria-label="Altered?">
          <button type="button" className="chip sm" aria-pressed={p.altered === false} onClick={() => set({ altered: false, alts: [] })}>
            No
          </button>
          <button type="button" className="chip sm" aria-pressed={p.altered === true} onClick={() => set({ altered: true })}>
            Yes
          </button>
        </div>
        {p.altered && (
          <div className="otiles">
            {[...picks, ...(tailoring.includes("Something else") ? ["Something else"] : [])].map((t) => {
              const on = p.alts.includes(t);
              return (
                <Tile key={t} kind="alt" name={t} pressed={on} onClick={() => set({ alts: on ? p.alts.filter((x) => x !== t) : [...p.alts, t] })}>
                  <GarmentSketch look={{ ...look, alts: [t] }} />
                </Tile>
              );
            })}
          </div>
        )}
        <p className="eyebrow wish-l">Wish it fit differently?</p>
        <textarea
          className="chatbox"
          rows={2}
          maxLength={500}
          placeholder="Like: gaps at the back of the waist, a little long"
          aria-label="Anything you wish fit differently"
          value={p.wish}
          onChange={(e) => {
            const text = cleanWish(e.target.value);
            set({ wish: e.target.value });
            window.clearTimeout(wishTimer.current);
            wishTimer.current = window.setTimeout(() => readWish(text), 900);
          }}
          onBlur={() => readWish(wishText)}
        />
        {wishShown.length > 0 && (
          <div className="wish-read">
            <p className="eyebrow">We'll allow for</p>
            <div className="row" role="group" aria-label="We'll allow for">
              {wishShown.map((id) => {
                const on = !p.wishOff.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    className="chip sm lite"
                    aria-pressed={on}
                    onClick={() => set({ wishOff: on ? [...p.wishOff, id] : p.wishOff.filter((x) => x !== id) })}
                  >
                    {wishLabel(id)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {wishText && p.wishFor === wishText && !p.wishRead.length && <p className="hint">Saved as a note. We'll learn from it as more people describe fits like this.</p>}
      </>
    );
  };

  return (
    <div className="rows">
      {rows.map((r) => {
        const done = rowDone(p, r);
        const isOpen = open === r;
        return (
          <div key={r} className={"frow" + (done ? " done" : "") + (isOpen ? " open" : "") + (OPTIONAL_ROWS.includes(r) ? " opt" : "")}>
            <button type="button" className="fhead" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : r)}>
              <span className="ck" aria-hidden="true" />
              <span className="fl">{ROW_NAME[r]}</span>
              <span className="fv">{summary(r)}</span>
            </button>
            {isOpen && <div className="fbody">{body(r)}</div>}
          </div>
        );
      })}
    </div>
  );
}

/** Describing the piece that fits well right on the board: what kind, the short list, then use it. */
export function DescribeForm(props: {
  cat: Category;
  brands: string[];
  tailoring: string[];
  wouldChange: { id: string; label: string }[];
  onDone: (d: Described) => void;
}) {
  const { cat, brands, tailoring, wouldChange, onDone } = props;
  const kinds = KINDS[cat];
  const [p, setP] = useState<PieceFields>(() => emptyPiece(kinds?.[0][0] ?? null));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ready = rowDone(p, "brand");

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      onDone(await describePiece(cat, p));
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      {kinds && (
        <div className="row" role="group" aria-label="What">
          {kinds.map(([v, l]) => (
            <button key={v} className="chip sm" type="button" aria-pressed={p.kind === v} onClick={() => setP((x) => ({ ...x, kind: v }))}>
              {l}
            </button>
          ))}
        </div>
      )}
      <DescribePiece cat={cat} value={p} onChange={setP} brands={brands} tailoring={tailoring} wouldChange={wouldChange} />
      <button className="btn primary" type="button" disabled={!ready || busy} onClick={() => void submit()}>
        Use this piece
      </button>
      {err && <p className="hint warn">{err}</p>}
    </div>
  );
}
